"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

interface CtaImage {
	src: string;
	altKey: string;
}

interface IndustryUploadCtaProps {
	namespace: string;
	images: readonly CtaImage[];
	brandColor?: string;
	largeImages?: boolean;
}

const CTA_PATTERN_LINE = "#eee4e2";
const CTA_PATTERN_GAP_PX = 80;

export default function IndustryUploadCta({
	namespace,
	images,
	brandColor = "#64a525",
	largeImages = false,
}: IndustryUploadCtaProps) {
	const t = useTranslations(namespace);

	return (
		<section className="relative overflow-hidden py-8 sm:py-10 md:py-12 lg:py-16">
			<div
				className="pointer-events-none absolute inset-0"
				style={{
					backgroundColor: "#ededed",
					backgroundImage: `repeating-linear-gradient(30deg, transparent, transparent ${CTA_PATTERN_GAP_PX}px, ${CTA_PATTERN_LINE} ${CTA_PATTERN_GAP_PX}px, ${CTA_PATTERN_LINE} ${
						CTA_PATTERN_GAP_PX + 1
					}px), repeating-linear-gradient(-30deg, transparent, transparent ${CTA_PATTERN_GAP_PX}px, ${CTA_PATTERN_LINE} ${CTA_PATTERN_GAP_PX}px, ${CTA_PATTERN_LINE} ${
						CTA_PATTERN_GAP_PX + 1
					}px), repeating-linear-gradient(90deg, transparent, transparent ${CTA_PATTERN_GAP_PX}px, ${CTA_PATTERN_LINE} ${CTA_PATTERN_GAP_PX}px, ${CTA_PATTERN_LINE} ${
						CTA_PATTERN_GAP_PX + 1
					}px)`,
				}}
			/>

			<div className="pointer-events-none absolute inset-0 z-[1] mx-auto w-full max-w-[1280px] px-4">
				<div className="absolute bottom-0 left-4 hidden items-end gap-6 lg:flex xl:left-6 xl:gap-8">
					{images.slice(0, 2).map((image, index) => (
						<div
							key={image.src}
							className={`${
								index === 0
									? largeImages
										? "w-[200px] -translate-y-4 xl:w-[235px] xl:-translate-y-6"
										: "w-[160px] -translate-y-4 xl:w-[190px] xl:-translate-y-6"
									: largeImages
										? "w-[175px] xl:w-[210px]"
										: "w-[140px] xl:w-[170px]"
							} drop-shadow-[0_16px_28px_rgba(0,0,0,0.18)]`}
						>
							<Image
								src={image.src}
								alt={t(image.altKey)}
								width={320}
								height={280}
								className="h-auto w-full object-contain object-bottom"
							/>
						</div>
					))}
				</div>

				{images[2] ? (
					<div
						className={`absolute bottom-0 right-4 hidden drop-shadow-[0_18px_32px_rgba(0,0,0,0.18)] lg:block xl:right-6 ${
							largeImages ? "w-[250px] xl:w-[300px]" : "w-[200px] xl:w-[240px]"
						}`}
					>
						<Image
							src={images[2].src}
							alt={t(images[2].altKey)}
							width={480}
							height={400}
							className="h-auto w-full object-contain object-bottom"
						/>
					</div>
				) : null}
			</div>

			<div className="container relative z-10">
				<div className="px-2 text-center">
					<h2 className="mb-6 text-2xl font-bold leading-tight text-black sm:mb-8 sm:text-[30px] md:mb-10 md:text-4xl lg:mb-12">
						<span className="block">{t("uploadCtaTitleLine1")}</span>
						<span className="block">{t("uploadCtaTitleLine2")}</span>
					</h2>

					<Link
						href="/upload"
						className="inline-flex items-center justify-center rounded px-8 py-3.5 text-base font-semibold text-white transition-all duration-300 hover:text-white hover:opacity-90 hover:shadow-md sm:px-10 sm:py-4 sm:text-lg"
						style={{ backgroundColor: brandColor }}
					>
						{t("uploadCtaButton")}
					</Link>
				</div>
			</div>
		</section>
	);
}
